import dayjs from "dayjs";

/**
 * Posição dos Fundos — relatório Commitments of Traders (COT) da CFTC.
 *
 * Usa o COT "Disaggregated", que separa Managed Money (fundos), Producer/
 * Merchant (produtores, tradings e processadoras) e Swap Dealers. Os dados
 * vêm direto da API pública da CFTC (aceita chamada do navegador e não pede
 * chave), a mesma usada no widget de COT do app.
 *
 * A posição é sempre de terça-feira; a CFTC divulga na sexta seguinte, às
 * 15h30 de Washington (em semana de feriado americano, na segunda).
 */

export const COT_RELATORIOS = {
  futuros: {
    label: "Somente futuros",
    url: "https://publicreporting.cftc.gov/resource/72hh-3qpy.json",
  },
  combinado: {
    label: "Futuros e opções",
    url: "https://publicreporting.cftc.gov/resource/kh3c-gbw2.json",
  },
};

// cftc_contract_market_code — conferidos na API em 04/11/2026.
export const COT_MERCADOS = [
  { code: "005602", label: "Soja", bolsa: "CBOT" },
  { code: "002602", label: "Milho", bolsa: "CBOT" },
  { code: "001602", label: "Trigo SRW", bolsa: "CBOT" },
  { code: "001612", label: "Trigo HRW (Kansas)", bolsa: "CBOT" },
  { code: "026603", label: "Farelo de Soja", bolsa: "CBOT" },
  { code: "007601", label: "Óleo de Soja", bolsa: "CBOT" },
  { code: "057642", label: "Boi Gordo", bolsa: "CME" },
  { code: "061641", label: "Boi Magro (Feeder)", bolsa: "CME" },
  { code: "054642", label: "Suíno (Lean Hogs)", bolsa: "CME" },
  { code: "052641", label: "Leite Classe III", bolsa: "CME" },
  { code: "083731", label: "Café Arábica", bolsa: "ICE NY" },
  { code: "080732", label: "Açúcar nº 11", bolsa: "ICE NY" },
  { code: "033661", label: "Algodão nº 2", bolsa: "ICE NY" },
  { code: "073732", label: "Cacau", bolsa: "ICE NY" },
  { code: "040701", label: "Suco de Laranja", bolsa: "ICE NY" },
];

const CAMPOS = {
  data: "report_date_as_yyyy_mm_dd",
  oi: "open_interest_all",
  oiVar: "change_in_open_interest_all",
  mmLong: "m_money_positions_long_all",
  mmShort: "m_money_positions_short_all",
  mmSpread: "m_money_positions_spread",
  mmLongVar: "change_in_m_money_long_all",
  mmShortVar: "change_in_m_money_short_all",
  mmLongPct: "pct_of_oi_m_money_long_all",
  mmShortPct: "pct_of_oi_m_money_short_all",
  prodLong: "prod_merc_positions_long",
  prodShort: "prod_merc_positions_short",
  swapLong: "swap_positions_long_all",
  // o nome da coluna na CFTC é com dois "_" mesmo
  swapShort: "swap__positions_short_all",
  tradersMmLong: "traders_m_money_long_all",
  tradersMmShort: "traders_m_money_short_all",
};

// O COT Index usa janela de 3 anos: busca 3 anos além do período exibido,
// para todas as semanas do gráfico terem a janela completa.
const ANOS_JANELA = 3;
const FOLGA_JANELA_DIAS = 14;

/** Semanas do contrato em ordem cronológica, com os campos já numéricos. */
export async function buscarCot(codigo, { relatorio = "futuros", semanas = 52 } = {}) {
  const desde = dayjs()
    .subtract(semanas, "week")
    .subtract(ANOS_JANELA, "year")
    .subtract(2, "week")
    .format("YYYY-MM-DD");
  const params = new URLSearchParams({
    $select: Object.values(CAMPOS).join(","),
    $where: `cftc_contract_market_code='${codigo}' AND ${CAMPOS.data} >= '${desde}'`,
    $order: `${CAMPOS.data} ASC`,
    $limit: "2000",
  });
  const resposta = await fetch(`${COT_RELATORIOS[relatorio].url}?${params}`);
  if (!resposta.ok) throw new Error(`CFTC respondeu ${resposta.status}`);
  const linhas = await resposta.json();
  return linhas.map((l) => {
    const semana = { data: String(l[CAMPOS.data]).slice(0, 10) };
    Object.entries(CAMPOS).forEach(([chave, campo]) => {
      if (chave !== "data") semana[chave] = Number(l[campo] ?? 0);
    });
    return semana;
  });
}

/**
 * Acrescenta os calculados: líquidos (fundos, produtores, swap), variação do
 * líquido na semana e o COT Index de 3 anos:
 * (líquido − mínimo 3a) ÷ (máximo 3a − mínimo 3a) × 100.
 */
export function calcularCot(semanas) {
  if (!semanas.length) return [];
  const primeira = dayjs(semanas[0].data);
  const liquidos = semanas.map((s) => s.mmLong - s.mmShort);
  return semanas.map((s, i) => {
    const liquido = liquidos[i];
    const inicioJanela = dayjs(s.data).subtract(ANOS_JANELA, "year");
    let indice = null;
    if (primeira.diff(inicioJanela, "day") <= FOLGA_JANELA_DIAS) {
      let min = liquido;
      let max = liquido;
      for (let j = i; j >= 0 && dayjs(semanas[j].data).isAfter(inicioJanela); j--) {
        min = Math.min(min, liquidos[j]);
        max = Math.max(max, liquidos[j]);
      }
      indice = max === min ? 50 : ((liquido - min) / (max - min)) * 100;
    }
    return {
      ...s,
      liquido,
      liquidoVar: s.mmLongVar - s.mmShortVar,
      prodLiquido: s.prodLong - s.prodShort,
      swapLiquido: s.swapLong - s.swapShort,
      indice,
    };
  });
}

export const dataBR = (iso) => (iso ? dayjs(iso).format("DD/MM/YYYY") : "");
export const dataCurta = (iso) => (iso ? dayjs(iso).format("DD/MM/YY") : "");

const inteiro = new Intl.NumberFormat("pt-BR");
export const fmtContratos = (v, sinal = false) =>
  v === null || v === undefined ? "—" : `${sinal && v > 0 ? "+" : ""}${inteiro.format(Math.round(v))}`;
export const fmtMil = (v) =>
  `${(Math.abs(v) / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil`;

export function zonaCot(indice) {
  if (indice === null || indice === undefined) return null;
  if (indice >= 90) return { cor: "green", texto: "Extremo comprado" };
  if (indice <= 10) return { cor: "red", texto: "Extremo vendido" };
  return { cor: "default", texto: "Sem extremo" };
}

/**
 * Leitura em texto da última semana, para a redação: quanto o líquido dos
 * fundos mudou, se veio de compras/vendas novas ou de liquidação, e se o
 * fluxo virou.
 */
export function leituraDaSemana(mercado, linhas) {
  const ultima = linhas[linhas.length - 1];
  if (!ultima) return [];
  const frases = [];
  const { mmLongVar: dL, mmShortVar: dS, liquidoVar: dN, liquido } = ultima;
  const lado = liquido >= 0 ? "comprados" : "vendidos";

  frases.push(
    `Na semana até ${dataBR(ultima.data)}, os fundos estavam ${lado} em ${fmtMil(liquido)} contratos líquidos de ${mercado}.`
  );
  if (dN === 0) {
    frases.push("A posição líquida ficou estável.");
  } else {
    frases.push(
      `No saldo da semana, ${dN > 0 ? "compraram" : "venderam"} ${fmtMil(dN)} contratos líquidos.`
    );
    const [parteA, nomeA, parteB, nomeB] =
      dN < 0
        ? [Math.max(0, -dL), "liquidação de posições compradas", Math.max(0, dS), "abertura de novas vendas"]
        : [Math.max(0, dL), "entrada de novas compras", Math.max(0, -dS), "recompra de posições vendidas"];
    const maior = parteA >= parteB ? nomeA : nomeB;
    const menor = parteA >= parteB ? nomeB : nomeA;
    const grande = Math.max(parteA, parteB);
    const pequena = Math.min(parteA, parteB);
    if (pequena === 0 || grande / pequena >= 1.5) frases.push(`O movimento veio principalmente da ${maior}, e não da ${menor}.`);
    else frases.push(`O movimento combinou ${nomeA} e ${nomeB} em proporções parecidas.`);
  }

  const anteriores = linhas.slice(-3, -1).map((l) => l.liquidoVar);
  if (dN !== 0 && anteriores.length === 2 && anteriores.every((p) => Math.sign(p) === -Math.sign(dN))) {
    frases.push(
      dN > 0
        ? "É a primeira semana de compras líquidas depois de duas semanas de vendas."
        : "É a primeira semana de vendas líquidas depois de duas semanas de compras."
    );
  }
  const zona = zonaCot(ultima.indice);
  if (zona && ultima.indice !== null) {
    frases.push(
      `O COT Index (3 anos) está em ${Math.round(ultima.indice)}${
        zona.texto === "Sem extremo" ? ", fora das zonas de extremo (abaixo de 10 ou acima de 90)." : ` — ${zona.texto.toLowerCase()}.`
      }`
    );
  }
  return frases;
}
